if (!customElements.get('byte-quote-builder')) {
  const STORAGE_KEY = 'byte-quote-builder-auth';

  class ByteQuoteBuilder extends HTMLElement {
    constructor() {
      super();

      this.products = [];
      this.tableItems = [];
      this.isAuthenticated = false;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandlePasswordSubmit = this.handlePasswordSubmit.bind(this);
      this.boundHandleTableInput = this.handleTableInput.bind(this);
      this.boundHandleDiscountInput = this.handleDiscountInput.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#checkAuthentication();
      this.#loadProducts();
      this.#setDefaultDate();
      this.#loadExchangeRate();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-quote-builder-action]');
      if (!actionTrigger || !this.contains(actionTrigger)) return;

      const builderAction = actionTrigger.dataset.quoteBuilderAction;

      if (builderAction === 'unlock') {
        event.preventDefault();
        this.#handlePasswordAttempt();
      }

      if (builderAction === 'add-product') {
        event.preventDefault();
        this.#addProductToTable();
      }

      if (builderAction === 'create-quote') {
        event.preventDefault();
        this.#createQuote();
      }

      if (builderAction === 'show-quotes') {
        event.preventDefault();
        this.#showQuotesModal();
      }

      if (builderAction === 'close-quotes') {
        event.preventDefault();
        this.#closeQuotesModal();
      }

      if (builderAction === 'remove-item') {
        event.preventDefault();
        const itemId = actionTrigger.dataset.itemId;
        this.#removeItem(itemId);
      }

      if (builderAction === 'download-quote') {
        event.preventDefault();
        const quoteId = actionTrigger.dataset.quoteId;
        this.#downloadSavedQuote(quoteId);
      }
    }

    handlePasswordSubmit(event) {
      event.preventDefault();
      this.#handlePasswordAttempt();
    }

    handleTableInput(event) {
      const quantityInput = event.target.closest('[data-item-quantity]');
      if (!quantityInput) return;

      const itemId = quantityInput.dataset.itemId;
      const item = this.tableItems.find((tableItem) => tableItem.id === itemId);
      if (!item) return;

      item.quantity = Math.max(1, Number(quantityInput.value) || 1);
      this.#renderTable();
    }

    handleDiscountInput() {
      this.#renderTable();
    }

    #cacheElements() {
      this.passwordPanel = this.querySelector('[data-quote-builder-password]');
      this.contentPanel = this.querySelector('[data-quote-builder-content]');
      this.passwordInput = this.querySelector('[data-quote-builder-password-input]');
      this.passwordError = this.querySelector('[data-quote-builder-password-error]');
      this.pdfTitleInput = this.querySelector('[data-quote-builder-pdf-title]');
      this.quoteDateInput = this.querySelector('[data-quote-builder-quote-date]');
      this.quoteProviderInput = this.querySelector('[data-quote-builder-quote-provider]');
      this.quoteNoteInput = this.querySelector('[data-quote-builder-quote-note]');
      this.discountInput = this.querySelector('[data-quote-builder-discount]');
      this.exchangeValue = this.querySelector('[data-quote-builder-exchange-value]');
      this.exchangeMeta = this.querySelector('[data-quote-builder-exchange-meta]');
      this.productSelect = this.querySelector('[data-quote-builder-product-select]');
      this.productQuantityInput = this.querySelector('[data-quote-builder-product-quantity]');
      this.tableBody = this.querySelector('[data-quote-builder-table-body]');
      this.tableFoot = this.querySelector('[data-quote-builder-table-foot]');
      this.tableWrap = this.querySelector('[data-quote-builder-table-wrap]');
      this.tableEmpty = this.querySelector('[data-quote-builder-table-empty]');
      this.quotesOverlay = this.querySelector('[data-quote-builder-quotes-overlay]');
      this.quotesList = this.querySelector('[data-quote-builder-quotes-list]');
      this.pagePassword = this.dataset.pagePassword || 'luna2024';
      this.vatRate = Number(this.dataset.vatRate) || 20;
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} TL';
      this.storageScope = this.dataset.storageScope || 'default';
      this.logoUrl = this.dataset.logoUrl || '';
      this.quotesApiUrl = this.dataset.quotesApiUrl || '';
      this.quotesApiSecret = this.dataset.quotesApiSecret || '';
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      this.passwordPanel?.addEventListener('submit', this.boundHandlePasswordSubmit);
      this.tableBody?.addEventListener('input', this.boundHandleTableInput);
      this.tableBody?.addEventListener('change', this.boundHandleTableInput);
      this.discountInput?.addEventListener('input', this.boundHandleDiscountInput);
      this.discountInput?.addEventListener('change', this.boundHandleDiscountInput);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.passwordPanel?.removeEventListener('submit', this.boundHandlePasswordSubmit);
      this.tableBody?.removeEventListener('input', this.boundHandleTableInput);
      this.tableBody?.removeEventListener('change', this.boundHandleTableInput);
      this.discountInput?.removeEventListener('input', this.boundHandleDiscountInput);
      this.discountInput?.removeEventListener('change', this.boundHandleDiscountInput);
    }

    #authStorageKey() {
      return `${STORAGE_KEY}:${this.storageScope}`;
    }

    #canUseQuotesApi() {
      return Boolean(this.quotesApiUrl && this.quotesApiSecret);
    }

    #getQuotesApiHeaders(includeJsonContentType = false) {
      const headers = {
        Authorization: `Bearer ${this.quotesApiSecret}`,
        Accept: 'application/json',
      };

      if (includeJsonContentType) {
        headers['Content-Type'] = 'application/json';
      }

      return headers;
    }

    async #fetchQuotesApi(path = '', options = {}) {
      const baseUrl = this.quotesApiUrl.replace(/\/$/, '');
      const url = `${baseUrl}${path}`;
      const hasBody = Boolean(options.body);

      return fetch(url, {
        ...options,
        headers: {
          ...this.#getQuotesApiHeaders(hasBody),
          ...options.headers,
        },
      });
    }

    #checkAuthentication() {
      const isAuthenticated = sessionStorage.getItem(this.#authStorageKey()) === 'true';
      this.isAuthenticated = isAuthenticated;
      this.#togglePanels(isAuthenticated);
    }

    #togglePanels(isAuthenticated) {
      this.passwordPanel?.toggleAttribute('hidden', isAuthenticated);
      this.contentPanel?.toggleAttribute('hidden', !isAuthenticated);

      if (isAuthenticated) {
        this.#loadExchangeRate();
      }
    }

    #handlePasswordAttempt() {
      const password = this.passwordInput?.value?.trim();

      if (password !== this.pagePassword) {
        this.passwordError?.removeAttribute('hidden');
        return;
      }

      sessionStorage.setItem(this.#authStorageKey(), 'true');
      this.isAuthenticated = true;
      this.passwordError?.setAttribute('hidden', '');
      this.#togglePanels(true);
    }

    #setDefaultDate() {
      if (!this.quoteDateInput || this.quoteDateInput.value) return;

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      this.quoteDateInput.value = `${year}-${month}-${day}`;
    }

    async #loadProducts() {
      if (!this.productSelect) return;

      try {
        this.products = await this.#fetchAllProducts();
        this.#populateProductSelect();
      } catch (error) {
        console.error(error);
      }
    }

    async #fetchAllProducts() {
      const allProducts = [];
      let page = 1;

      while (true) {
        const response = await fetch(`/products.json?limit=250&page=${page}`);
        if (!response.ok) break;

        const data = await response.json();
        allProducts.push(...(data.products || []));

        if (!data.products || data.products.length < 250) break;
        page += 1;
      }

      return allProducts;
    }

    #populateProductSelect() {
      const fragment = document.createDocumentFragment();
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = this.dataset.selectPlaceholder || '';
      fragment.appendChild(defaultOption);

      for (const product of this.products) {
        const option = document.createElement('option');
        option.value = product.id;
        option.textContent = product.title;
        fragment.appendChild(option);
      }

      this.productSelect.innerHTML = '';
      this.productSelect.appendChild(fragment);
    }

    #getProductById(productId) {
      return this.products.find((product) => String(product.id) === String(productId));
    }

    #stripHtml(html) {
      const element = document.createElement('div');
      element.innerHTML = html || '';
      return element.textContent?.trim() || '';
    }

    #escapeAttr(text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    #formatMoney(cents) {
      const amount = (Number(cents) / 100).toFixed(2);
      return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(cents) / 100));
    }

    #formatAmount(amount) {
      return `${Number(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
    }

    #calculateLineTotals(priceIncVatCents, quantity, vatRate = this.vatRate) {
      const unitPriceIncVat = priceIncVatCents / 100;
      const unitPriceExVat = unitPriceIncVat / (1 + vatRate / 100);
      const subtotalIncVat = unitPriceIncVat * quantity;
      const subtotalExVat = unitPriceExVat * quantity;
      const vatAmount = subtotalIncVat - subtotalExVat;

      return {
        unitPriceIncVat,
        unitPriceExVat,
        subtotalIncVat,
        subtotalExVat,
        vatAmount,
        vatRate,
      };
    }

    #getDiscountPercent() {
      const discountPercent = Number(this.discountInput?.value);
      if (!Number.isFinite(discountPercent)) return 0;
      return Math.min(100, Math.max(0, discountPercent));
    }

    #calculateQuoteTotals(items, discountPercent = this.#getDiscountPercent()) {
      const vat10Amount = items
        .filter((item) => Number(item.vatRate) === 10)
        .reduce((sum, item) => sum + item.vatAmount, 0);
      const vat20Amount = items
        .filter((item) => Number(item.vatRate) === 20)
        .reduce((sum, item) => sum + item.vatAmount, 0);
      const totalVatAmount = items.reduce((sum, item) => sum + item.vatAmount, 0);
      const grandTotalExVat = items.reduce((sum, item) => sum + item.subtotalExVat, 0);
      const grandTotalIncVat = items.reduce((sum, item) => sum + item.subtotalIncVat, 0);
      const discountRate = discountPercent / 100;
      const discountAmountExVat = grandTotalExVat * discountRate;
      const discountAmountIncVat = grandTotalIncVat * discountRate;
      const discountVatAmount = totalVatAmount * discountRate;

      return {
        discountPercent,
        discountAmountExVat,
        discountAmountIncVat,
        discountVatAmount,
        vat10Amount: vat10Amount * (1 - discountRate),
        vat20Amount: vat20Amount * (1 - discountRate),
        totalVatAmount: totalVatAmount * (1 - discountRate),
        grandTotalExVat: grandTotalExVat * (1 - discountRate),
        grandTotalIncVat: grandTotalIncVat * (1 - discountRate),
        subtotalExVat: grandTotalExVat,
        subtotalIncVat: grandTotalIncVat,
        subtotalVat10Amount: vat10Amount,
        subtotalVat20Amount: vat20Amount,
        subtotalTotalVatAmount: totalVatAmount,
      };
    }

    async #loadExchangeRate() {
      if (!this.exchangeValue || !this.exchangeMeta) return;

      this.exchangeValue.textContent = '—';
      this.exchangeMeta.textContent = this.dataset.exchangeLoading || 'Yükleniyor...';

      if (typeof ByteTcmbExchange === 'undefined') {
        this.exchangeMeta.textContent = this.dataset.exchangeError || 'Kur yüklenemedi.';
        return;
      }

      try {
        const rateData = await ByteTcmbExchange.fetchUsdRate(this.dataset.tcmbApiUrl);
        this.exchangeValue.textContent = `${rateData.rate.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺`;
        this.exchangeMeta.textContent = `${rateData.source || this.dataset.exchangeSource || 'TCMB'} · ${rateData.date}`;
      } catch {
        this.exchangeValue.textContent = '—';
        this.exchangeMeta.textContent = this.dataset.exchangeError || 'Kur yüklenemedi.';
      }
    }

    #addProductToTable() {
      const productId = this.productSelect?.value;
      const quantity = Math.max(1, Number(this.productQuantityInput?.value) || 1);

      if (!productId) {
        window.alert(this.dataset.errorNoProduct);
        return;
      }

      if (this.tableItems.some((item) => String(item.productId) === String(productId))) {
        window.alert(this.dataset.errorProductExists);
        return;
      }

      const product = this.#getProductById(productId);
      if (!product) return;

      const variant = product.variants?.[0];
      const priceCents = Math.round(Number(variant?.price || 0) * 100);

      this.tableItems.push({
        id: `${product.id}-${Date.now()}`,
        productId: product.id,
        title: product.title,
        image: product.images?.[0]?.src || '',
        priceCents,
        vatRate: this.vatRate,
        stock: variant?.inventory_quantity ?? '-',
        quantity,
        description: this.#stripHtml(product.body_html),
        variantId: variant?.id,
      });

      this.productSelect.value = '';
      if (this.productQuantityInput) this.productQuantityInput.value = '1';
      this.#renderTable();
    }

    #removeItem(itemId) {
      this.tableItems = this.tableItems.filter((item) => item.id !== itemId);
      this.#renderTable();
    }

    #renderTable() {
      if (!this.tableBody) return;

      this.tableBody.innerHTML = '';

      if (!this.tableItems.length) {
        this.tableEmpty?.removeAttribute('hidden');
        this.tableWrap?.setAttribute('hidden', '');
        this.tableFoot?.setAttribute('hidden', '');
        return;
      }

      this.tableEmpty?.setAttribute('hidden', '');
      this.tableWrap?.removeAttribute('hidden');

      const renderedItems = this.tableItems.map((item) => ({
        ...item,
        ...this.#calculateLineTotals(item.priceCents, item.quantity, item.vatRate),
      }));
      const totals = this.#calculateQuoteTotals(renderedItems);

      for (const item of renderedItems) {
        const row = document.createElement('tr');
        row.className = 'byte-quote-builder__table-row';

        row.innerHTML = `
          <td class="byte-quote-builder__table-cell byte-quote-builder__table-cell--image" data-label="">
            ${
              item.image
                ? `<img src="${item.image}" alt="${item.title.replace(/"/g, '&quot;')}" class="byte-quote-builder__table-image" width="56" height="56" loading="lazy">`
                : ''
            }
          </td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colName)}">${item.title}</td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colPrice)}">${this.#formatMoney(item.priceCents)}</td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colVat)}">${item.vatRate.toFixed(2)}%</td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colStock)}">${item.stock}</td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colQty)}">
            <input
              type="number"
              min="1"
              value="${item.quantity}"
              data-item-quantity
              data-item-id="${item.id}"
              aria-label="${this.dataset.labelQuantity || 'Quantity'}"
            >
          </td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colDescription)}"><div class="byte-quote-builder__table-description">${item.description}</div></td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colExVat)}">${this.#formatAmount(item.subtotalExVat)}</td>
          <td class="byte-quote-builder__table-cell" data-label="${this.#escapeAttr(this.dataset.colIncVat)}">${this.#formatAmount(item.subtotalIncVat)}</td>
          <td class="byte-quote-builder__table-cell byte-quote-builder__table-cell--actions" data-label="${this.#escapeAttr(this.dataset.colActions)}">
            <button
              type="button"
              class="byte-quote-builder__remove-button"
              data-quote-builder-action="remove-item"
              data-item-id="${item.id}"
            >
              ${this.dataset.labelRemove || 'Remove'}
            </button>
          </td>
        `;

        this.tableBody.appendChild(row);
      }

      if (this.tableFoot) {
        this.tableFoot.removeAttribute('hidden');
        this.tableFoot.innerHTML = `
          <tr class="byte-quote-builder__table-totals">
            <td colspan="10">
              <div class="byte-quote-builder__totals">
                <strong>${this.dataset.labelTotalsTitle || 'Toplamlar'}</strong>
                <p>${this.dataset.labelVat10 || '%10 KDV Tutarı'}: ${this.#formatAmount(totals.vat10Amount)}</p>
                <p>${this.dataset.labelVat20 || '%20 KDV Tutarı'}: ${this.#formatAmount(totals.vat20Amount)}</p>
                <p>${this.dataset.labelTotalVat || 'Toplam KDV Tutarı'}: ${this.#formatAmount(totals.totalVatAmount)}</p>
                ${
                  totals.discountPercent > 0
                    ? `<p>${this.dataset.labelDiscount || 'İskonto'} (%${totals.discountPercent}): -${this.#formatAmount(totals.discountAmountIncVat)}</p>`
                    : ''
                }
                <p>${this.dataset.labelGrandExVat || 'KDV Hariç Toplam'}: ${this.#formatAmount(totals.grandTotalExVat)}</p>
                <p>${this.dataset.labelGrandIncVat || 'KDV Dahil Toplam'}: ${this.#formatAmount(totals.grandTotalIncVat)}</p>
              </div>
            </td>
          </tr>
        `;
      }
    }

    #validateForm() {
      return Boolean(
        this.pdfTitleInput?.value?.trim() &&
          this.quoteDateInput?.value &&
          this.quoteProviderInput?.value &&
          this.tableItems.length
      );
    }

    #buildQuotePayload() {
      const items = this.tableItems.map((item) => ({
        ...item,
        ...this.#calculateLineTotals(item.priceCents, item.quantity, item.vatRate),
      }));

      return {
        id: `quote-${Date.now()}`,
        pdfTitle: this.pdfTitleInput.value.trim(),
        quoteDate: this.quoteDateInput.value,
        quoteProvider: this.quoteProviderInput.value,
        quoteNote: this.quoteNoteInput?.value?.trim() || '',
        discountPercent: this.#getDiscountPercent(),
        items,
        totals: this.#calculateQuoteTotals(items),
        createdAt: new Date().toISOString(),
      };
    }

    async #createQuote() {
      if (!this.#validateForm()) {
        window.alert(this.dataset.errorMissingFields);
        return;
      }

      if (!this.#canUseQuotesApi()) {
        window.alert(this.dataset.errorQuotesNotConfigured);
        return;
      }

      const quote = this.#buildQuotePayload();

      try {
        const savedQuote = await this.#saveQuote(quote);
        if (savedQuote?.handle) {
          quote.id = savedQuote.handle;
        }

        await this.#generateAndDownloadPdf(quote);
      } catch (error) {
        window.alert(this.dataset.errorQuotesSave);
        console.error(error);
      }
    }

    async #saveQuote(quote) {
      const response = await this.#fetchQuotesApi('', {
        method: 'POST',
        body: JSON.stringify(quote),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.error || `Save failed: ${response.status}`);
      }

      return responseData;
    }

    async #getSavedQuotes() {
      const response = await this.#fetchQuotesApi('');

      if (!response.ok) {
        throw new Error(`Load failed: ${response.status}`);
      }

      const data = await response.json();
      return data.quotes || [];
    }

    async #fetchQuoteByHandle(handle) {
      const response = await this.#fetchQuotesApi(`/${encodeURIComponent(handle)}`);

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status}`);
      }

      return response.json();
    }

    async #generateAndDownloadPdf(quote) {
      const safeTitle = quote.pdfTitle.replace(/[^\w\s-çğıöşüÇĞİÖŞÜ]/g, '').trim() || 'teklif';
      const pdfGenerator = new ByteQuotePdfGenerator({
        logoUrl: this.logoUrl,
        labels: {
          quoteDate: this.dataset.labelQuoteDate,
          quoteProvider: this.dataset.labelQuoteProvider,
          quoteNote: this.dataset.labelQuoteNote,
          colImage: this.dataset.colImage,
          colName: this.dataset.colName,
          colPrice: this.dataset.colPrice,
          colQty: this.dataset.colQty,
          colPrint: this.dataset.colPrint,
          colDescription: this.dataset.colDescription,
          colVat: this.dataset.colVat,
          colExVat: this.dataset.colExVat,
          colIncVat: this.dataset.colIncVat,
          totalsTitle: this.dataset.labelTotalsTitle,
          vat10: this.dataset.labelVat10,
          vat20: this.dataset.labelVat20,
          totalVat: this.dataset.labelTotalVat,
          grandExVat: this.dataset.labelGrandExVat,
          grandIncVat: this.dataset.labelGrandIncVat,
          discount: this.dataset.labelDiscount,
          discountAmount: this.dataset.labelDiscountAmount,
        },
      });

      await pdfGenerator.download(quote, `${safeTitle}.pdf`);
    }

    async #showQuotesModal() {
      if (!this.quotesList || !this.quotesOverlay) return;

      if (!this.#canUseQuotesApi()) {
        window.alert(this.dataset.errorQuotesNotConfigured);
        return;
      }

      this.quotesList.innerHTML = '';
      this.quotesOverlay.removeAttribute('hidden');

      const loadingItem = document.createElement('li');
      loadingItem.className = 'byte-quote-builder__quotes-item';
      loadingItem.textContent = this.dataset.labelQuotesLoading || 'Loading...';
      this.quotesList.appendChild(loadingItem);

      try {
        const savedQuotes = await this.#getSavedQuotes();
        this.quotesList.innerHTML = '';

        if (!savedQuotes.length) {
          const emptyItem = document.createElement('li');
          emptyItem.className = 'byte-quote-builder__quotes-item';
          emptyItem.textContent = this.dataset.errorNoQuotes || 'No saved quotes.';
          this.quotesList.appendChild(emptyItem);
        } else {
          for (const quote of savedQuotes) {
            const quoteId = quote.handle || quote.id;
            const listItem = document.createElement('li');
            listItem.className = 'byte-quote-builder__quotes-item';
            listItem.innerHTML = `
              <div class="byte-quote-builder__quotes-item-info">
                <strong>${this.#escapeAttr(quote.pdfTitle)}</strong><br>
                ${this.#escapeAttr(quote.quoteDate)} · ${this.#escapeAttr(quote.quoteProvider)}
              </div>
              <button type="button" class="button button--secondary" data-quote-builder-action="download-quote" data-quote-id="${this.#escapeAttr(quoteId)}">
                ${this.dataset.labelDownload || 'Download'}
              </button>
            `;
            this.quotesList.appendChild(listItem);
          }
        }
      } catch (error) {
        this.quotesList.innerHTML = '';
        const errorItem = document.createElement('li');
        errorItem.className = 'byte-quote-builder__quotes-item';
        errorItem.textContent = this.dataset.errorQuotesLoad || 'Saved quotes could not be loaded.';
        this.quotesList.appendChild(errorItem);
        console.error(error);
      }
    }

    #closeQuotesModal() {
      this.quotesOverlay?.setAttribute('hidden', '');
    }

    async #downloadSavedQuote(quoteId) {
      if (!quoteId) return;

      if (!this.#canUseQuotesApi()) {
        window.alert(this.dataset.errorQuotesNotConfigured);
        return;
      }

      try {
        const quote = await this.#fetchQuoteByHandle(quoteId);

        if (!quote.totals) {
          quote.totals = this.#calculateQuoteTotals(quote.items);
        }

        await this.#generateAndDownloadPdf(quote);
      } catch (error) {
        window.alert(this.dataset.errorQuotesLoad);
        console.error(error);
      }
    }
  }

  customElements.define('byte-quote-builder', ByteQuoteBuilder);
}
